/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IoClose, IoDownload, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { FaFilePdf, FaFileWord, FaFileExcel, FaFile } from 'react-icons/fa';
import { MdZoomIn, MdZoomOut, MdRotateRight } from 'react-icons/md';
import './PreviewAnexos.css';
import DeleteImageInspeccao from '../admin/departamentos/dlif/inspeccao/avfd/deleteImageInspeccao';

interface PreviewAnexosProps {
  files: string[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (index: number) => void;
  setMessageSuccess: () => void;
  setMessageError: () => void;
  setRefresh: () => void;
  inspeccao: any;
}

const PreviewAnexos: React.FC<PreviewAnexosProps> = ({
  files,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  inspeccao,
  setMessageSuccess,
  setMessageError,
  setRefresh,
}) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  const currentFile = files?.[currentIndex] || '';
  const fileName = currentFile.split('/').pop()?.split('?')[0] || 'arquivo';
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';

  // const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);
  const isImage =
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension) ||
    /^armed_[a-z0-9]+$/i.test(fileName);
  const isPdf = fileExtension === 'pdf';
  const isWord = ['doc', 'docx'].includes(fileExtension);
  const isExcel = ['xls', 'xlsx'].includes(fileExtension);

  const resetControls = () => {
    setZoom(100);
    setRotation(0);
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 300));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 50));
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handlePrevious = () => {
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
      resetControls();
    }
  };

  const handleNext = () => {
    if (currentIndex < files.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1);
      resetControls();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = currentFile;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, handleNext, handlePrevious, onClose]);

  if (!isOpen || !files.length) return null;

  const renderContent = () => {
    if (isImage) {
      return (
        <img
          src={currentFile}
          alt={fileName}
          className="preview-image"
          style={{
            transform: `scale(${zoom / 20}) rotate(${rotation}deg)`
          }}
        />
      );
    }
    if (isPdf) {
      return <iframe src={currentFile} className="preview-frame" title={fileName} />;
    }
    if (isWord || isExcel) {
      const viewer = `https://docs.google.com/viewer?url=${encodeURIComponent(
        currentFile
      )}&embedded=true`;
      return <iframe src={viewer} className="preview-frame" title={fileName} />;
    }
    return (
      <div className="preview-fallback">
        <div className="preview-icon">
          {isPdf ? <FaFilePdf /> : isWord ? <FaFileWord /> : isExcel ? <FaFileExcel /> : <FaFile />}
        </div>
        <p>{fileName}</p>
        <button onClick={handleDownload} className="download-btn">
          <IoDownload /> Baixar arquivo
        </button>
      </div>
    );
  };

  return createPortal(
    <div className="preview-overlay">
      <div className="preview-header">
        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
          <span className="file-name">
            {fileName}
          </span>
          {!inspeccao?.estabelecimento?.approved && <div style={{ position: 'absolute', top: '25px' }}>
            <DeleteImageInspeccao
              index={inspeccao + 1}
              inspeccaoId={inspeccao?._id}
              setMessageSuccess={setMessageSuccess}
              setMessageError={setMessageError}
              setRefresh={setRefresh}
            />
          </div>}
        </div>
        <div className="header-buttons">
          {isImage && (
            <>
              <button onClick={handleZoomOut} title="Diminuir zoom">
                <MdZoomOut />
              </button>
              <span className="zoom-value">{zoom}%</span>
              <button onClick={handleZoomIn} title="Aumentar zoom">
                <MdZoomIn />
              </button>
              <button onClick={handleRotate} title="Rotacionar">
                <MdRotateRight />
              </button>
            </>
          )}
          <button onClick={handleDownload} title="Baixar">
            <IoDownload />
          </button>
          <button onClick={onClose} title="Fechar">
            <IoClose />
          </button>
        </div>
      </div>

      <div className="preview-content">
        {currentIndex > 0 && (
          <button className="nav-btn left" onClick={handlePrevious}>
            <IoChevronBack />
          </button>
        )}
        {renderContent()}
        {currentIndex < files.length - 1 && (
          <button className="nav-btn right" onClick={handleNext}>
            <IoChevronForward />
          </button>
        )}
      </div>

      {files.length > 1 && (
        <div className="preview-footer">
          {files.map((file, index) => {
            const ext = file.split('.').pop()?.toLowerCase() || '';
            const isThumbImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            return (
              <button
                key={index}
                className={`thumb ${index === currentIndex ? 'active' : ''}`}
                onClick={() => onNavigate && onNavigate(index)}
              >
                {isThumbImage ? (
                  <img src={file} alt={`thumb-${index}`} />
                ) : (
                  <div className="thumb-icon">
                    {ext === 'pdf' ? (
                      <FaFilePdf />
                    ) : ['doc', 'docx'].includes(ext) ? (
                      <FaFileWord />
                    ) : ['xls', 'xlsx'].includes(ext) ? (
                      <FaFileExcel />
                    ) : (
                      <FaFile />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>,
    document.body
  );
};

export default PreviewAnexos;
